import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { useMy } from '@/hooks/useMy';
import { queryClient } from '@/queryclient';
import { AssetStatus } from '@/types';
import { useMutation } from '@/utils/api';

interface UpdateSettingProps {
  name: string;
  value?: string;
}

export function useScanLevel({ email }: { email?: string } = {}) {
  const axios = useAxios({ email });

  const {
    data: settings,
    isPending: isLoadingScanLevel,
    status,
    refetch,
  } = useMy(
    { resource: 'setting' },
    {
      email,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always' as const,
    }
  );

  const currentScanLevel =
    (settings?.find(s => s.name === 'scan-level')?.value as AssetStatus) ??
    AssetStatus.Active;
  const isFrozen = settings?.find(s => s.name === 'frozen')?.value === 'true';

  const updateSetting = useMutation<void, Error, UpdateSettingProps>({
    defaultErrorMessage: 'Failed to update setting',
    mutationFn: async ({ name, value }) => {
      const promise = axios.put('/setting', {
        name,
        value,
      });

      const toastMessages =
        name === 'scan-level'
          ? {
              loading: value ? 'Updating scan level' : 'Removing scan level',
              success: value
                ? 'Scan level updated successfully'
                : 'Scan level removed successfully',
              error: 'Failed to update scan level',
            }
          : {
              loading:
                value === 'true'
                  ? 'Pausing all scanning activity'
                  : 'Resuming scanning activity',
              success:
                value === 'true'
                  ? 'All scanning activity paused'
                  : 'Scanning activity resumed',
              error: 'Failed to update scanning status',
            };

      toast.promise(promise, toastMessages);

      await promise;
      await refetch();
      await queryClient.invalidateQueries({ queryKey: ['my', 'setting'] });
    },
  });

  const updateScanLevel = async (status: AssetStatus) => {
    return updateSetting.mutateAsync({
      name: 'scan-level',
      value: status,
    });
  };

  const removeScanLevel = async () => {
    return updateSetting.mutateAsync({
      name: 'scan-level',
    });
  };

  const freeze = async () => {
    return updateSetting.mutateAsync({
      name: 'frozen',
      value: 'true',
    });
  };

  const unfreeze = async () => {
    return updateSetting.mutateAsync({
      name: 'frozen',
      value: 'false',
    });
  };

  return {
    updateSetting: updateSetting.mutate,
    updateScanLevel,
    removeScanLevel,
    freeze,
    unfreeze,
    status,
    isUpdating: updateSetting.isPending,
    error: updateSetting.error,
    currentScanLevel,
    isFrozen,
    isLoadingScanLevel,
  };
}
